import {
  NodoDto,
  getGetNodoPortaleQueryOptions,
  useDownloadImmagineDinamicaNodo,
} from "@/api";
import {
  DispositivoRefetchInterval,
  getDispositivoColor,
} from "@/utils/dispositivoUtils";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueries } from "@tanstack/react-query";
import { getPortaleId } from "@/utils/portaleUtils";

export type ImmagineDinamicaContenitoreCardProps = {
  nodo: NodoDto;
};

export default function ImmagineDinamicaContenitoreCard({
  nodo,
}: ImmagineDinamicaContenitoreCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const portaleId = getPortaleId();
  const svgFileId = nodo.immagineDinamicaSvgId ?? 0;

  const {
    data: svgData,
    isLoading: isSvgLoading,
    error: svgError,
  } = useDownloadImmagineDinamicaNodo(nodo.id, {
    axios: { responseType: "blob" },
  });

  const [svgContent, setSvgContent] = useState<string>("");
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  const nodiElementiQueries = useQueries({
    queries: (nodo.immagineDinamicaElementi ?? []).map((elemento) => {
      const options = getGetNodoPortaleQueryOptions(
        portaleId,
        elemento.nodoDispositivoId
      );
      return {
        ...options,
        refetchInterval: DispositivoRefetchInterval,
      };
    }),
  });

  const dispositiviById = useMemo(() => {
    const map = new Map<number, NodoDto>();
    nodiElementiQueries.forEach((result) => {
      if (result.data?.data) {
        const nodo = result.data.data;
        if (
          nodo.tipo === "Dispositivo_Macchinario" ||
          nodo.tipo === "Dispositivo_Sensore"
        ) {
          map.set(nodo.id, nodo);
        }
      }
    });
    return map;
  }, [nodiElementiQueries]);

  const isLoading =
    isSvgLoading || nodiElementiQueries.some((r) => r.isLoading);
  const error = svgError || nodiElementiQueries.some((r) => r.isError);

  useEffect(() => {
    if (svgData?.data) {
      const reader = new FileReader();
      reader.onload = () => {
        setSvgContent(reader.result as string);
      };
      reader.readAsText(svgData.data as Blob);
    }
  }, [svgData]);

  useEffect(() => {
    if (!container) {
      return;
    }

    const svgElement = container.querySelector("svg");
    if (svgElement) {
      svgElement.style.maxWidth = "100%";
      svgElement.style.height = "auto";
    }

    (nodo.immagineDinamicaElementi ?? []).forEach((elemento) => {
      const dispositivo = dispositiviById.get(elemento.nodoDispositivoId);
      if (!dispositivo) {
        return;
      }

      const targetElement = container.querySelector(
        `#${CSS.escape(elemento.svgElementId)}`
      );

      if (!targetElement) {
        return;
      }

      const connected =
        !!dispositivo.dispositivoLastStatus &&
        ["on", "on-manuale", "alarm"].includes(
          dispositivo.dispositivoLastStatus
        );

      const deviceColor = connected
        ? getDispositivoColor(dispositivo, {
            sourceLastColor: true,
            sourceNovusDiagnostic: true,
          })?.color
        : null;
      const color = deviceColor ?? "#808080";

      // Colore
      const colorRect = targetElement.cloneNode(true) as SVGElement;
      colorRect.style.fill = color;
      colorRect.style.pointerEvents = "none";

      // Click -> apertura del nodo
      const clickRect = targetElement.cloneNode(true) as SVGElement;
      clickRect.style.fill = "transparent";
      clickRect.style.cursor = "pointer";
      clickRect.addEventListener("click", () => {
        navigate(`/nodi/${dispositivo.id}`);
      });

      // tooltip
      const existingTitles = clickRect.querySelectorAll("title");
      existingTitles.forEach((title) => title.remove());

      const titleElement = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "title"
      );
      titleElement.textContent = [
        // dispositivo.nome,
        dispositivo.dispositivoMatricola,
      ]
        .filter((x) => !!x)
        .join(" - ");
      clickRect.appendChild(titleElement);

      // Sostituiamo l'originale con il clone del colore e aggiungiamo quello di interazione
      const parent = targetElement.parentNode;
      if (parent) {
        parent.replaceChild(colorRect, targetElement);
        parent.appendChild(clickRect);
      }
    });
  }, [
    container,
    dispositiviById,
    navigate,
    nodo.immagineDinamicaElementi,
    svgContent,
  ]);

  if (!svgFileId) {
    return (
      <Card sx={{ width: "100%" }}>
        <CardContent>
          <Typography color="text.secondary">
            {t("nodoPage.immagineDinamica.mancante")}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ width: "100%" }}>
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 200,
            width: "100%",
          }}
        >
          {isLoading && <CircularProgress />}
          {error && (
            <Typography color="error">
              {t("nodoPage.immagineDinamica.errore")}
            </Typography>
          )}
          {svgContent && !isLoading && (
            <div
              ref={setContainer}
              dangerouslySetInnerHTML={{ __html: svgContent }}
              style={{
                width: "100%",
                height: "100%",
                maxHeight: "500px",
                display: "flex",
                justifyContent: "center",
              }}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
